import {
  Divider,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { LP_MANUAL_ACTION_LIST } from 'adminConstants/routes';
import Button from 'components/Button';
import ErrorBoundary from 'components/ErrorBoundary';
import AllocationsManager from 'components/new/AllocationsManager';
import Page from 'components/new/Page';
import SplitTransactionDialog from 'components/new/SplitTransactionDialog';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import { uniqBy } from 'lodash';
import { Controller } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';

import CounterpartySelector from 'components/new/CounterpartySelector';
import PageLoader from 'components/PageLoader';
import useEarliestAvailableLedgerDate from 'hooks/lp/data/vehicle/useEarliestAvailableLedgerDate';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import ReconcilableTransactions from './ReconcilableTransactions';
import Transaction from './Transaction';
import useManualActionForm, { FormValues } from './useManualActionForm';
import useReconcilableTransactions from './useReconcilableTransactions';

type Props = {
  vehicleId: string;
};

const AddEditManualAction: React.FC<Props> = ({ vehicleId }) => {
  const { vehicle } = useLayoutContext();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const earliestAvailableLedgerDate = useEarliestAvailableLedgerDate({
    vehicleId,
  });

  const {
    reconcilableTransactions,
    setSelectedTransactions,
    transactionToSplit,
    splitSelectedTransaction,
    showSplitTransactionDialog,
    closeSplitTransactionDialog,
    selectTransactionToSplit,
  } = useReconcilableTransactions(vehicleId);

  const [contacts] = api.contact.list.useSuspenseQuery();

  const { data: manualAction, isLoading } =
    api.manualJournal.getManualAction.useQuery(
      {
        id: id ?? '',
        vehicleId,
      },
      {
        enabled: !!id,
      },
    );

  if (manualAction?.transactions.some((t) => !!t.reconcilableTransactionId)) {
    throw new Error('Updating manual reconciliation is not allowed.');
  }

  const createManualAction = api.manualJournal.createManualAction.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Manual journal created successfully.',
      successRedirect: LP_MANUAL_ACTION_LIST,
    }),
  );

  const updateManualAction = api.manualJournal.updateManualAction.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Manual journal updated successfully.',
      successRedirect: LP_MANUAL_ACTION_LIST,
    }),
  );

  const {
    control,
    handleSubmit,
    formValues,
    formState,
    formTransactions,
    transactionsError,
    handleRemoveTransaction,
    handleAddTransaction,
    handleAllocationRuleChange,
    handleAllocationsChange,
    handleTransactionTypeChange,
    handleDebitAmountChange,
    handleCreditAmountChange,
  } = useManualActionForm({
    manualAction,
    reconcilableTransactions,
  });

  const isSavePending =
    createManualAction.isPending || updateManualAction.isPending;
  const isInReconciliationMode = reconcilableTransactions.some(
    (t) => t.isSelected,
  );

  const onSubmit = (data: FormValues) => {
    if (isSavePending) return;

    if (!id) {
      createManualAction.mutate({
        vehicleId,
        ...data,
      });
    } else {
      updateManualAction.mutate({
        id,
        vehicleId,
        ...data,
      });
    }
  };

  const handleCancel = () => {
    history.push(LP_MANUAL_ACTION_LIST);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const reconcilableTransactionsForAllocations = uniqBy(
    formValues.transactions.filter((t) => !!t.reconcilableTransactionId),
    (t) => t.reconcilableTransactionId,
  ).map((t) => ({
    transactionId: t.reconcilableTransactionId ?? '',
    transactionAmountTcy: t.amountTcy,
  }));

  return (
    <Page
      tabTitle="Manual Journal: Add Manual Journal"
      spacing="lg"
      heading="Add manual journal"
      withHeadingDivider
      goBackAction={LP_MANUAL_ACTION_LIST}
      actionBar={
        <Row fullWidth centered justify="space-between">
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isSavePending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            loading={isSavePending}
          >
            Save
          </Button>
        </Row>
      }
    >
      <form>
        <Column spacing="lg" fullWidth>
          <FormField label="Date" size="md">
            <Controller
              name="ledgerDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  fullWidth
                  {...field}
                  value={field.value}
                  error={!!error}
                  helperText={error?.message}
                  minDate={earliestAvailableLedgerDate?.toUTCDate()}
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Counterparty">
            <Controller
              name="counterparty"
              control={control}
              render={({
                fieldState: { error },
                field: { onChange, ...field },
              }) => (
                <CounterpartySelector
                  contacts={contacts}
                  error={error}
                  placeholder="Select counterparty"
                  onChange={(value) => {
                    onChange(value);
                  }}
                  {...field}
                />
              )}
            />
          </FormField>
          <FormField label="Description" size="md">
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <Divider />
          <Typography variant="h5">Link to a transaction</Typography>
          <ReconcilableTransactions
            reconcilableTransactions={reconcilableTransactions}
            onTransactionSelection={setSelectedTransactions}
            onSelectTransactionToSplit={selectTransactionToSplit}
          />
          <Divider />
          <Typography variant="h5">Transactions</Typography>
          <Grid container spacing={2}>
            <Grid size="grow">
              <Typography variant="h6" color="text.secondary">
                Transaction type
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="h6" color="text.secondary">
                Debit
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="h6" color="text.secondary">
                Credit
              </Typography>
            </Grid>
            <Grid size={1}></Grid>
          </Grid>
          {formTransactions.map((transaction, index) => (
            <Transaction
              key={transaction.fieldId}
              index={index}
              transaction={transaction}
              transactionErrors={formState.errors.transactions?.[index]}
              vehicleId={vehicleId}
              currency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
              onTransctionTypeChange={handleTransactionTypeChange}
              onDebitAmountChange={handleDebitAmountChange}
              onCreditAmountChange={handleCreditAmountChange}
              onRemove={handleRemoveTransaction}
              disableRemove={isInReconciliationMode}
            />
          ))}
          {transactionsError ? (
            <FormHelperText error>{transactionsError}</FormHelperText>
          ) : null}
          <div>
            <Button
              variant="outlined"
              size="large"
              onClick={handleAddTransaction}
              disabled={isInReconciliationMode}
            >
              Add another transaction
            </Button>
          </div>
          <Divider />
          {formValues.totalAmountTcy > 0 && (
            <ErrorBoundary>
              <AllocationsManager
                vehicleId={vehicleId}
                allocations={formValues.allocations}
                allocatedActionLabel="Manual journal"
                allocatedAmountTcy={formValues.totalAmountTcy}
                currency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
                baseCurrency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
                allocationRule={formValues.allocationRule}
                onAllocationRuleChange={handleAllocationRuleChange}
                onAllocationsChange={handleAllocationsChange}
                error={formState.errors.allocations?.message}
                currentActionIds={id ? [id] : undefined}
                date={formValues.ledgerDate}
                reconcilableTransactions={
                  reconcilableTransactionsForAllocations
                }
              />
            </ErrorBoundary>
          )}
        </Column>
        {showSplitTransactionDialog ? (
          <SplitTransactionDialog
            onClose={closeSplitTransactionDialog}
            transactionUnreconciledAmountTcy={
              transactionToSplit?.unreconciledAmountTcy ?? 0
            }
            onSplit={splitSelectedTransaction}
            currency={transactionToSplit?.transactionCurrency}
          />
        ) : null}
      </form>
    </Page>
  );
};

const AddEditManualActionWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <AddEditManualAction vehicleId={selectedVehicleId} />;
};

export default AddEditManualActionWrapper;
